import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Loader2, Bell, CheckSquare, MessageCircle, FileText, Building2, Search, Users } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

import AlertsPage from './AlertsPage';
import TasksPage from './TasksPage';
import ChatPage from './ChatPage';
import BriefPage from './BriefPage';
import MyBusinessPage from './MyBusinessPage';
import AuditPage from './AuditPage';
import TeamPage from './TeamPage';
import MetricsDashboard from './MetricsDashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const Dashboard = () => {
  const [alerts, setAlerts] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [connectorStatus, setConnectorStatus] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [followersNew7d, setFollowersNew7d] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    loadAll();
    const interval = setInterval(loadAll, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const loadAll = async () => {
    await Promise.all([
      loadAlerts(),
      loadTasks(),
      loadConnectorStatus(),
      loadMetrics(),
      loadFollowersNew7d(),
    ]);
    setLoading(false);
  };

  const loadAlerts = async () => {
    try { const r = await axios.get(`${BACKEND_URL}/api/alerts`); setAlerts(r.data || []); } catch (e) {}
  };
  const loadTasks = async () => {
    try { const r = await axios.get(`${BACKEND_URL}/api/tasks/pending`); setTasks(r.data || []); } catch (e) {}
  };
  const loadConnectorStatus = async () => {
    try { const r = await axios.get(`${BACKEND_URL}/api/connectors/status`); setConnectorStatus(r.data); } catch (e) {}
  };
  const loadMetrics = async () => {
    try { const r = await axios.get(`${BACKEND_URL}/api/connectors/metrics`); setMetrics(r.data); } catch (e) {}
  };
  const loadFollowersNew7d = async () => {
    try { const r = await axios.get(`${BACKEND_URL}/api/connectors/followers/new7d`); setFollowersNew7d(r.data); } catch (e) {}
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const unreadAlerts = alerts.filter(a => !a.acknowledged);
  const pendingTasks = tasks.filter(t => !t.completed);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/agentes-tu-codigo-base.webp" alt="Tu Código Base" className="h-10 w-10 rounded-lg object-cover" onError={(e) => { e.target.style.display = "none"; }} />
            <div>
              <h1 className="text-xl font-bold text-slate-900">Tu Equipo Virtual 24/7</h1>
              <p className="text-xs text-slate-500">tucodigobase.com</p>
            </div>
          </div>
          <Badge className="bg-green-500 text-white flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-white animate-pulse"></div>
            SISTEMA ACTIVO
          </Badge>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-4">
        <MetricsDashboard
          metrics={metrics}
          connectorStatus={connectorStatus?.status}
          followersNew7d={followersNew7d}
          onRefresh={loadAll}
          toast={toast}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 pb-8">
  <Tabs defaultValue="alerts" className="space-y-4">
    <TabsList className="flex flex-wrap gap-1">
      <TabsTrigger value="alerts" className="flex items-center gap-1">
        <Bell className="h-4 w-4" /> Alertas
        {unreadAlerts.length > 0 && <Badge className="bg-red-500 text-white text-xs ml-1">{unreadAlerts.length}</Badge>}
      </TabsTrigger>
      <TabsTrigger value="tasks" className="flex items-center gap-1">
        <CheckSquare className="h-4 w-4" /> Tareas
        {pendingTasks.length > 0 && <Badge className="bg-amber-500 text-white text-xs ml-1">{pendingTasks.length}</Badge>}
      </TabsTrigger>
      <TabsTrigger value="chat" className="flex items-center gap-1"><MessageCircle className="h-4 w-4" /> Chat</TabsTrigger>
      <TabsTrigger value="team" className="flex items-center gap-1"><Users className="h-4 w-4" /> Equipo</TabsTrigger>
      <TabsTrigger value="brief" className="flex items-center gap-1"><FileText className="h-4 w-4" /> Brief</TabsTrigger>
      <TabsTrigger value="my-business" className="flex items-center gap-1"><Building2 className="h-4 w-4" /> Mi Negocio</TabsTrigger>
      <TabsTrigger value="audit" className="flex items-center gap-1"><Search className="h-4 w-4" /> Auditoría 360</TabsTrigger>
    </TabsList>
    <TabsContent value="alerts"><AlertsPage alerts={alerts} onRefresh={loadAlerts} toast={toast} /></TabsContent>
    <TabsContent value="tasks"><TasksPage tasks={tasks} onRefresh={loadTasks} toast={toast} /></TabsContent>
    <TabsContent value="chat"><ChatPage toast={toast} /></TabsContent>
    <TabsContent value="team"><TeamPage /></TabsContent>
    <TabsContent value="brief"><BriefPage toast={toast} /></TabsContent>
    <TabsContent value="my-business"><MyBusinessPage toast={toast} /></TabsContent>
    <TabsContent value="audit"><AuditPage toast={toast} /></TabsContent>
  </Tabs>
</div>
</div>
  );
};

export default Dashboard;
